// components/steps/CourseStep.tsx
import React, { useState, useEffect } from 'react';
import { api } from '../../config/api.ts';
import { Course, University } from '../../types';
import { useDebounce } from '../../hooks/useDebounce.ts';
import { AutocompleteInput } from '../ui/AutocompleteInput.tsx';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { SearchIcon, ChevronLeftIcon } from '../icons/index.tsx';

interface CourseStepProps {
  selectedUniversity: University;
  onSelect: (course: Course) => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

export const CourseStep: React.FC<CourseStepProps> = ({
  selectedUniversity,
  onSelect,
  onBack,
  setError,
  setLoading
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [searchingCourses, setSearchingCourses] = useState(false);
  
  const debouncedCourseSearch = useDebounce(courseSearch, 300);

  useEffect(() => {
    if (selectedUniversity && debouncedCourseSearch.length > 0) {
      searchCourses(selectedUniversity.university_id, debouncedCourseSearch);
    } else if (selectedUniversity && debouncedCourseSearch.length === 0) {
      searchCourses(selectedUniversity.university_id, '');
    }
  }, [selectedUniversity, debouncedCourseSearch]);

  const searchCourses = async (universityId: number, query: string = '') => {
    try {
      setSearchingCourses(true);
      const response = await api.get(`/public/courses/search?university_id=${universityId}&query=${query}&limit=20`);
      if (response.data.success) {
        setCourses(response.data.data.courses);
      }
    } catch (err) {
      setError('فشل في البحث عن المواد');
    } finally {
      setSearchingCourses(false);
    }
  };

  // Helper function to format course display name
  const formatCourseDisplayName = (course: any) => {
    if (course.real_name && course.name && course.real_name !== course.name) {
      return `${course.real_name} (${course.name})`;
    }
    return course.real_name || course.name;
  };

  // Helper function to format subtitle with both names if different
  const formatCourseSubtitle = (course: any) => {
    const baseSubtitle = `${course.plans_count} خطة متاحة • ${course.department_name}`;
    
    // If we have both names and they're different, we've already shown both in the main title
    // So just show the standard subtitle
    return baseSubtitle;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">اختر المادة</h2>
          <p className="text-gray-600">{selectedUniversity?.name}</p>
        </div>
        <ModernButton 
          variant="outline"
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
      </div>
      
      <AutocompleteInput
        label="البحث عن المواد"
        placeholder="اكتب اسم أو رمز المادة..."
        value={courseSearch}
        onChange={setCourseSearch}
        onSelect={onSelect}
        options={courses.map(course => ({
          ...course,
          display_name: formatCourseDisplayName(course),
          subtitle: formatCourseSubtitle(course)
        }))}
        loading={searchingCourses}
        displayKey="display_name"
        icon={<SearchIcon />}
      />
    </div>
  );
};